import {
  ProductionDayBatch,
  Recipe,
  Product,
  ProductionPhase,
  ProductionPhaseType,
  BatchMaterialLotRow,
} from "../../../types/mes";
import { formatClockTime } from "../../../utils/processParameterSerialize";
import { isAbsoluteRecipe } from "../../../utils/recipeQuantity";
import { compareLocale, dateLocale, t } from "../../../i18n/locale";

/** Legacy DB time columns a phase code maps to (when the engine only emits the
 * legacy 5-phase schedule, i.e. the production-day endpoint). */
type LegacyTimeKey = "mixing" | "fermentation" | "processing" | "final_proof" | "baking";

interface PhaseDisplay {
  name: string;
  /** Whether this phase is normally bound to a machine (mixer/stroj/peć…). */
  usesMachine: boolean;
  /** Operator role printed on the signature line. */
  operator: string;
  /** Legacy time column this phase falls back to (if any). */
  legacy?: LegacyTimeKey;
}

/** Canonical dynamic production-phase model (mirrors backend PHASE_CODE +
 * frontend PHASE_META). Used for display names + legacy time fallbacks. */
export const PHASE_DISPLAY: Record<string, PhaseDisplay> = {
  mixing: { name: "Miješanje", usesMachine: true, operator: "Miješač", legacy: "mixing" },
  dividing: { name: "Dijeljenje", usesMachine: true, operator: "Operater stroja" },
  bulk_fermentation: { name: "Fermentacija", usesMachine: false, operator: "Operater", legacy: "fermentation" },
  moulding: { name: "Oblikovanje", usesMachine: true, operator: "Operater stroja" },
  processing: { name: "Obrada", usesMachine: true, operator: "Operater stroja", legacy: "processing" },
  final_proof: { name: "Završno dizanje", usesMachine: false, operator: "Operater", legacy: "final_proof" },
  baking: { name: "Pečenje", usesMachine: true, operator: "Pečar", legacy: "baking" },
  cooling: { name: "Hlađenje", usesMachine: false, operator: "Operater" },
  packaging: { name: "Pakiranje", usesMachine: true, operator: "Pakirer" },
};

/** Canonical phase order for tabs/legend. */
export const PHASE_ORDER = [
  "mixing",
  "dividing",
  "bulk_fermentation",
  "moulding",
  "processing",
  "final_proof",
  "baking",
  "cooling",
  "packaging",
];

/** Per-product phase configuration distilled to what the work order needs. */
export interface ProductPhaseLite {
  code: string;
  machineId?: number | null;
  machineName?: string | null;
  usesMachine?: boolean;
  durationMin?: number | null;
  tempC?: number | null;
}

export type ProductPhaseMap = Record<number, ProductPhaseLite[]>;

/** Normalize the API ProductionPhase[] of one product into ProductPhaseLite[]. */
export const toProductPhaseLite = (phases: ProductionPhase[]): ProductPhaseLite[] =>
  phases
    .map((p) => {
      const type = p.ProductionPhaseType_ProductionPhaseToPhaseType;
      const code = type?.Code ?? "";
      if (!code) return null;
      return {
        code,
        machineId: p.Machine ?? null,
        machineName: p.Machine_ProductionPhaseToMachine?.MachineName ?? null,
        usesMachine: type?.UsesMachine ?? undefined,
        durationMin: p.DurationMin != null ? Number(p.DurationMin) : null,
        tempC: p.TempC != null ? Number(p.TempC) : null,
      } as ProductPhaseLite;
    })
    .filter((p): p is ProductPhaseLite => p !== null);

// ---- helpers ---------------------------------------------------------------

const esc = (value: unknown): string =>
  String(value ?? "").replace(/[&<>"]/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return c;
    }
  });

const fmtTime = (iso?: string | null): string => formatClockTime(iso, "—");

const fmtDate = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(dateLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtNum = (n: number, decimals = 2): string =>
  Number.isFinite(n) ? n.toFixed(decimals) : "—";

const cfgFor = (productPhases: ProductPhaseMap, productId?: number): ProductPhaseLite[] =>
  (productId != null && productPhases[productId]) || [];

/** Display name for a phase code, preferring the DB phase-type Name. */
export const phaseLabel = (code: string, phaseTypes?: ProductionPhaseType[]): string => {
  const fromType = phaseTypes?.find((t) => t.Code === code)?.Name;
  const name = fromType || PHASE_DISPLAY[code]?.name || code;
  return t(name);
};

/** Resolve a phase's scheduled times for a batch (dynamic segment → legacy column). */
export const resolvePhaseTimes = (
  batch: ProductionDayBatch,
  code: string
): { start?: string; end?: string } => {
  const seg = batch.Phases?.find((p) => p.Code === code);
  if (seg) return { start: seg.Start, end: seg.End };

  const legacy = PHASE_DISPLAY[code]?.legacy;
  switch (legacy) {
    case "mixing": return { start: batch.MixingStart, end: batch.MixingEnd };
    case "fermentation": return { start: batch.FermentationStart, end: batch.FermentationEnd };
    case "processing": return { start: batch.ProcessingStart, end: batch.ProcessingEnd };
    case "final_proof": return { start: batch.FinalProofStart, end: batch.FinalProofEnd };
    case "baking": return { start: batch.BakingStart, end: batch.BakingEnd };
    default: return {};
  }
};

/** Machine name driving the grouping of a phase's work order. */
export const phaseMachineName = (
  batch: ProductionDayBatch,
  code: string,
  productPhases: ProductPhaseMap
): string | null => {
  const p = cfgFor(productPhases, batch.ProductID).find((x) => x.code === code);
  if (p?.machineName) return p.machineName;
  if (code === "mixing") return batch.MixerName ?? null;
  if (code === "processing") return batch.ProcessName ?? null;
  if (code === "baking") return batch.OvenName ?? null;
  return null;
};

/** Whether a batch participates in a phase (product config → legacy fallback). */
export const batchHasPhase = (
  batch: ProductionDayBatch,
  code: string,
  productPhases: ProductPhaseMap
): boolean => {
  const cfg = cfgFor(productPhases, batch.ProductID);
  if (cfg.length > 0) return cfg.some((p) => p.code === code);
  // No configured phases (e.g. semi-finished) → fall back to legacy schedule.
  return !!resolvePhaseTimes(batch, code).start;
};

const phaseTempMin = (
  batch: ProductionDayBatch,
  code: string,
  productPhases: ProductPhaseMap,
  products: Record<number, Product>
): { temp?: number | null; min?: number | null } => {
  const cfg = cfgFor(productPhases, batch.ProductID).find((x) => x.code === code);
  const product = batch.ProductID ? products[batch.ProductID] : undefined;
  if (code === "baking") {
    return {
      temp: cfg?.tempC ?? product?.BakingTemp ?? null,
      min: cfg?.durationMin ?? batch.BakingTimeMin ?? product?.BakingTime ?? null,
    };
  }
  return { temp: cfg?.tempC ?? null, min: cfg?.durationMin ?? null };
};

const sortByStart = (batches: ProductionDayBatch[], code: string): ProductionDayBatch[] =>
  [...batches].sort((a, b) => {
    const sa = resolvePhaseTimes(a, code).start;
    const sb = resolvePhaseTimes(b, code).start;
    if (sa && sb) return new Date(sa).getTime() - new Date(sb).getTime();
    if (sa) return -1;
    if (sb) return 1;
    return (a.Sequence ?? 0) - (b.Sequence ?? 0);
  });

const groupBy = <T>(items: T[], key: (item: T) => string): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const arr = map.get(k);
    if (arr) arr.push(item);
    else map.set(k, [item]);
  }
  return map;
};

// ---- recipe (mixing) -------------------------------------------------------

const recipeLineInfo = (
  ing: NonNullable<Recipe["RecipeIngredient_RecipeToRecipeIngredient_Recipe"]>[number]
) => {
  const ingredient = ing.Ingredient_IngredientToRecipeIngredient;
  const child = ing.Recipe_RecipeToRecipeIngredient_ChildRecipe;
  if (ingredient) {
    return {
      name: ingredient.IngredientName || "—",
      type:
        ingredient.IngredientTypeName ||
        ingredient.IngredientType_IngredientToIngredientType?.Name ||
        "",
    };
  }
  // Semi-finished lines carry the product name on the child recipe; RecipeCode
  // is an internal code like "SF-6" and is only a last-resort fallback.
  if (child) {
    return {
      name: child.SemiFinishedProduct?.Name || child.RecipeCode || `Polup. #${child.ID}`,
      type: t("Poluproizvod"),
    };
  }
  return { name: "—", type: "" };
};

/** Dodijeljeni lotovi po zamjesu (BatchLotConsumption), za kolonu LOT. */
export type LotsByBatch = Record<number, BatchMaterialLotRow[]>;

const normName = (s?: string | null) => (s || "").trim().toLowerCase();

/**
 * Lotovi jedne recepturne stavke kroz sve zamjese jednog proizvoda na stranici.
 *
 * Sirovine se vežu po ID-u; poluproizvodi nemaju Ingredient na utrošku (u mikser
 * ulaze kao lot vlastite zamjese), pa se vežu po nazivu.
 */
const lotsForRecipeLine = (
  line: NonNullable<Recipe["RecipeIngredient_RecipeToRecipeIngredient_Recipe"]>[number],
  batches: ProductionDayBatch[],
  lotsByBatch: LotsByBatch
): { label: string; perBatch: boolean } => {
  const name = normName(recipeLineInfo(line).name);
  const matches = (row: BatchMaterialLotRow) =>
    line.Ingredient != null && row.ingredientId != null
      ? row.ingredientId === line.Ingredient
      : normName(row.material) === name;

  // Redoslijed zamjesa se čuva da se lot i zamjesa poklope u tablici ispod.
  const perBatchLots = batches.map((b) => {
    const rows = (lotsByBatch[b.ID] ?? []).filter(matches);
    const labels = rows.map((r) => r.lotNumber || (r.materialLotId ? `#${r.materialLotId}` : "")).filter(Boolean);
    return { mixCode: b.MixCode, labels };
  });

  const distinct = Array.from(new Set(perBatchLots.flatMap((p) => p.labels)));
  if (distinct.length === 0) return { label: "", perBatch: false };
  // Planiranje po zadanom dodijeli jedan lot na sve zamjese proizvoda; kad se
  // lot potroši u pola dana, zamjese ga imaju različit i tada se ispisuje
  // razrada po zamjesu (ispod tablice), a ne nabacani popis u jednoj ćeliji.
  // Isto vrijedi kad lot ima samo dio zamjesa — jedan lot u zaglavlju bi tvrdio
  // da vrijedi za sve, pa i taj slučaj ide u razradu.
  const allCovered = perBatchLots.every((p) => p.labels.length > 0);
  if (distinct.length === 1 && allCovered) return { label: distinct[0], perBatch: false };
  return { label: "po zamjesu (vidi ispod)", perBatch: true };
};

const renderRecipeTable = (
  recipe: Recipe,
  finalMass: number,
  batches: ProductionDayBatch[],
  lotsByBatch: LotsByBatch
): string => {
  const lines = recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe ?? [];
  if (lines.length === 0) return "";

  // Skaliranje nize dolje dijeli masu zamjesa zbrojem postotaka. Slasticarska
  // receptura nema postotaka, pa bi svaka stavka ispala 0,000 kg — radni nalog
  // pun nula operater cita kao recepturu. Radije se kaze da ispisa nema.
  if (isAbsoluteRecipe(recipe)) {
    return `
      <p class="muted">
        Receptura je u apsolutnim kolicinama (g / kom / list). Ispis radnog naloga
        za slasticarske recepture jos nije podrzan.
      </p>`;
  }

  const ordered = [...lines].sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));
  const sumPct = ordered.reduce((acc, l) => acc + (Number(l.BakerPercentage) || 0), 0);
  const factor = sumPct > 0 ? finalMass / sumPct : 0;

  /** Stavke kojima se lot razlikuje po zamjesu — razrada ispod tablice. */
  const perBatchLines: { name: string; rows: { mixCode: string; label: string }[] }[] = [];

  const rows = ordered
    .map((line, i) => {
      const info = recipeLineInfo(line);
      const pct = Number(line.BakerPercentage) || 0;
      const lot = lotsForRecipeLine(line, batches, lotsByBatch);

      if (lot.perBatch) {
        perBatchLines.push({
          name: info.name,
          rows: batches.map((b) => ({
            mixCode: b.MixCode,
            label:
              (lotsByBatch[b.ID] ?? [])
                .filter((r) =>
                  line.Ingredient != null && r.ingredientId != null
                    ? r.ingredientId === line.Ingredient
                    : normName(r.material) === normName(info.name)
                )
                .map((r) => r.lotNumber || (r.materialLotId ? `#${r.materialLotId}` : ""))
                .filter(Boolean)
                .join(", ") || "—",
          })),
        });
      }

      // Bez dodijeljenog lota ćelija ostaje prazna za ručni upis — tako je nalog
      // upotrebljiv i kad planiranje nije prošlo kroz dodjelu lotova.
      const lotCell = lot.label
        ? `<td><strong>${esc(lot.label)}</strong></td>`
        : `<td class="fill"></td>`;

      return `
        <tr>
          <td class="c">${i + 1}</td>
          <td><strong>${esc(info.name)}</strong></td>
          <td>${esc(info.type)}</td>
          <td class="c">${fmtNum(pct, 1)}</td>
          <td class="c"><strong>${fmtNum(pct * factor, 2)}</strong></td>
          ${lotCell}
        </tr>`;
    })
    .join("");

  const perBatchTables = perBatchLines
    .map(
      (line) => `
      <div class="lot-detail">
        <div class="lot-detail-title">${t("LOT po zamjesu")} — ${esc(line.name)}</div>
        <table class="wo-table">
          <thead>
            <tr>
              <th style="width:40%">${t("Šifra zamjesa")}</th>
              <th style="width:60%">LOT</th>
            </tr>
          </thead>
          <tbody>
            ${line.rows
              .map(
                (r) => `
              <tr>
                <td><strong>${esc(r.mixCode)}</strong></td>
                <td>${esc(r.label)}</td>
              </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </div>`
    )
    .join("");

  return `
    <table class="wo-table">
      <thead>
        <tr>
          <th style="width:5%">#</th>
          <th style="width:33%">${t("Sirovina")}</th>
          <th style="width:15%">${t("Tip")}</th>
          <th style="width:11%">${t("PEK %")}</th>
          <th style="width:14%">${t("Kg / zamjes")}</th>
          <th style="width:22%">LOT</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        <tr class="summary">
          <td colspan="4">${t("UKUPNO")}</td>
          <td class="c">${fmtNum(finalMass, 2)}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
    ${perBatchTables}`;
};

// ---- page renderers --------------------------------------------------------

const renderMixingGroup = (
  machine: string,
  batches: ProductionDayBatch[],
  recipes: Record<number, Recipe>,
  dateStr: string,
  lotsByBatch: LotsByBatch
): string => {
  const sorted = sortByStart(batches, "mixing");
  const totalKg = sorted.reduce((acc, b) => acc + (Number(b.FinalMassKg) || 0), 0);
  const byProduct = groupBy(sorted, (b) => `${b.ProductID ?? 0}|${b.ProductName ?? ""}`);

  let body = "";
  byProduct.forEach((prodBatches) => {
    const first = prodBatches[0];
    const mass = Number(first.FinalMassKg) || 0;
    const recipe = first.ProductID ? recipes[first.ProductID] : undefined;
    const start = fmtTime(resolvePhaseTimes(prodBatches[0], "mixing").start);
    const end = fmtTime(resolvePhaseTimes(prodBatches[prodBatches.length - 1], "mixing").end);

    const trackingRows = prodBatches
      .map(
        (b, i) => `
        <tr>
          <td class="c">${i + 1}</td>
          <td><strong>${esc(b.MixCode)}</strong></td>
          <td class="c">${fmtTime(resolvePhaseTimes(b, "mixing").start)} / </td>
          <td class="c">${fmtTime(resolvePhaseTimes(b, "mixing").end)} / </td>
          <td class="fill"></td>
          <td class="fill"></td>
          <td class="fill"></td>
        </tr>`
      )
      .join("");

    body += `
      <div class="section-title">${esc(first.ProductName)}</div>
      <div class="batch-bar">
        <span>${t("Zamjesa:")} <strong>${prodBatches.length} × ${fmtNum(mass, 0)} kg</strong></span>
        <span>${t("Plan:")} ${start} — ${end}</span>
      </div>
      ${
        recipe
          ? renderRecipeTable(recipe, mass, prodBatches, lotsByBatch)
          : `<div class="muted">${t("Receptura nije dostupna.")}</div>`
      }
      <table class="wo-table">
        <thead>
          <tr>
            <th style="width:5%">#</th>
            <th style="width:23%">${t("Šifra zamjesa")}</th>
            <th style="width:13%">${t("Plan poč. / stv.")}</th>
            <th style="width:13%">${t("Plan kraj / stv.")}</th>
            <th style="width:14%">${t("Temp. tijesta (°C)")}</th>
            <th style="width:12%">pH</th>
            <th style="width:20%">${t("Napomena")}</th>
          </tr>
        </thead>
        <tbody>${trackingRows}</tbody>
      </table>`;
  });

  return wrapPage("mixing", machine, dateStr, sorted.length, `${t("Ukupno:")} ${fmtNum(totalKg, 0)} kg`, body);
};

const renderGenericGroup = (
  code: string,
  machine: string,
  batches: ProductionDayBatch[],
  products: Record<number, Product>,
  productPhases: ProductPhaseMap,
  dateStr: string
): string => {
  const sorted = sortByStart(batches, code);
  const isBaking = code === "baking";
  const isProcessing = code === "processing";

  const extraHead = isBaking
    ? `<th style="width:9%">${t("Temp (°C)")}</th><th style="width:9%">${t("Vrijeme (min)")}</th>`
    : isProcessing
    ? `<th style="width:10%">${t("Odvaga (g)")}</th>`
    : "";

  const napomenaWidth = isBaking ? 19 : isProcessing ? 25 : 33;

  const rows = sorted
    .map((b, i) => {
      const t = resolvePhaseTimes(b, code);
      let extraCells = "";
      if (isBaking) {
        const tm = phaseTempMin(b, code, productPhases, products);
        extraCells = `<td class="c">${tm.temp != null ? esc(tm.temp) : ""}</td><td class="c">${tm.min != null ? esc(tm.min) : ""}</td>`;
      } else if (isProcessing) {
        const product = b.ProductID ? products[b.ProductID] : undefined;
        extraCells = `<td class="c">${product?.UnitWeightG != null ? esc(product.UnitWeightG) : ""}</td>`;
      }
      return `
        <tr>
          <td class="c">${i + 1}</td>
          <td><strong>${esc(b.MixCode)}</strong></td>
          <td>${esc(b.ProductName)}</td>
          <td class="c">${fmtTime(t.start)} / </td>
          <td class="c">${fmtTime(t.end)} / </td>
          ${extraCells}
          <td class="fill"></td>
        </tr>`;
    })
    .join("");

  const body = `
    <table class="wo-table">
      <thead>
        <tr>
          <th style="width:4%">#</th>
          <th style="width:18%">${t("Šifra zamjesa")}</th>
          <th style="width:20%">${t("Proizvod")}</th>
          <th style="width:11%">${t("Plan poč. / stv.")}</th>
          <th style="width:11%">${t("Plan kraj / stv.")}</th>
          ${extraHead}
          <th style="width:${napomenaWidth}%">${t("Napomena")}</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;

  return wrapPage(code, machine, dateStr, sorted.length, "", body);
};

const wrapPage = (
  code: string,
  machine: string,
  dateStr: string,
  count: number,
  extraMeta: string,
  body: string
): string => {
  const display = PHASE_DISPLAY[code];
  const phaseName = display?.name || code;
  const operator = t(display?.operator || "Operater");
  const heading = machine || t(phaseName);
  return `
    <div class="wo-page">
      <div class="wo-header">
        <div class="wo-title">
          <div class="wo-doctype">${t("RADNI NALOG")} — ${esc(t(phaseName).toUpperCase())}</div>
          <h1>${esc(heading)}</h1>
        </div>
        <div class="wo-meta">
          ${t("Datum:")} <strong>${fmtDate(dateStr)}</strong><br>
          ${t("Broj zamjesa:")} <strong>${count}</strong>
          ${extraMeta ? `<br>${extraMeta}` : ""}
        </div>
      </div>
      ${body}
      <div class="wo-signatures">
        <div>${t("Izdao (tehnolog)")}</div>
        <div>${esc(operator)}</div>
        <div>${t("Kontrola")}</div>
      </div>
      <div class="wo-footnote">
        ${t("Generirano iz Poweriot MES sustava. Sva odstupanja od naloga upisati u napomenu.")}
      </div>
    </div>`;
};

const PRINT_CSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #000;
    background: #f5f5f5;
  }
  .wo-page {
    background: #fff;
    width: 210mm;
    min-height: 297mm;
    margin: 10px auto;
    padding: 14mm 14mm 18mm;
    box-shadow: 0 1px 6px rgba(0,0,0,0.2);
  }
  .wo-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-bottom: 3px solid #000;
    padding-bottom: 8px;
    margin-bottom: 14px;
  }
  .wo-doctype { font-size: 12px; letter-spacing: 1px; color: #555; font-weight: bold; }
  .wo-title h1 { font-size: 22px; margin: 2px 0 0; }
  .wo-meta { text-align: right; font-size: 12px; line-height: 1.5; }
  .section-title {
    background: #333; color: #fff; padding: 6px 12px;
    font-size: 14px; font-weight: bold; margin: 18px 0 0;
  }
  .batch-bar {
    background: #ececec; padding: 5px 12px; font-size: 12px;
    display: flex; justify-content: space-between; margin-bottom: 6px;
  }
  table.wo-table {
    width: 100%; border-collapse: collapse; margin: 6px 0 10px; font-size: 11px;
  }
  table.wo-table th {
    background: #f0f0f0; border: 1px solid #999; padding: 5px 7px;
    text-align: left; font-weight: bold;
  }
  table.wo-table td { border: 1px solid #999; padding: 5px 7px; }
  table.wo-table td.c { text-align: center; }
  table.wo-table td.fill { height: 24px; }
  table.wo-table tr.summary td { background: #f0f0f0; font-weight: bold; }
  .muted { color: #777; font-style: italic; padding: 6px 2px; }
  .lot-detail { margin: 4px 0 10px; }
  .lot-detail-title {
    font-size: 11px; font-weight: bold; text-transform: uppercase;
    letter-spacing: 0.4px; color: #444; margin-bottom: 2px;
  }
  .wo-signatures {
    margin-top: 36px; display: flex; justify-content: space-between; gap: 24px;
  }
  .wo-signatures div {
    border-top: 1px solid #000; width: 30%; text-align: center;
    padding-top: 4px; font-size: 11px;
  }
  .wo-footnote {
    margin-top: 18px; padding-top: 8px; border-top: 1px solid #ccc;
    font-size: 10px; color: #777;
  }
  @media print {
    body { background: #fff; }
    .wo-page {
      width: auto; min-height: auto; margin: 0; box-shadow: none;
      padding: 0; page-break-after: always;
    }
    .wo-page:last-child { page-break-after: avoid; }
  }
  @page { size: A4; margin: 12mm; }
`;

export interface BuildArgs {
  phaseCode: string;
  dateStr: string; // YYYY-MM-DD
  batches: ProductionDayBatch[]; // already filtered to this phase
  recipes: Record<number, Recipe>;
  products: Record<number, Product>;
  productPhases: ProductPhaseMap;
  phaseTypes?: ProductionPhaseType[];
  /**
   * Lotovi sirovina potvrđeni u planiranju (i naknadno izmijenjeni u pogonu),
   * po zamjesu. Bez njih kolona LOT ostaje prazna za ručni upis.
   */
  lotsByBatch?: LotsByBatch;
}

/**
 * Builds a self-contained printable "radni nalog" document for one production
 * phase. Batches are grouped by the machine that performs that phase; the
 * mixing phase additionally prints the recipe per product. Returns the full
 * HTML string (to write into a new window) or "" when there is nothing to print.
 */
export const buildWorkOrderDocument = ({
  phaseCode,
  dateStr,
  batches,
  recipes,
  products,
  productPhases,
  phaseTypes,
  lotsByBatch = {},
}: BuildArgs): string => {
  if (!batches.length) return "";

  const phaseName = phaseLabel(phaseCode, phaseTypes);
  const byMachine = groupBy(batches, (b) => phaseMachineName(b, phaseCode, productPhases) || "");
  const machineNames = Array.from(byMachine.keys()).sort((a, b) => a.localeCompare(b, compareLocale()));

  const pages = machineNames
    .map((machine) => {
      const groupBatches = byMachine.get(machine) ?? [];
      if (phaseCode === "mixing")
        return renderMixingGroup(machine, groupBatches, recipes, dateStr, lotsByBatch);
      return renderGenericGroup(phaseCode, machine, groupBatches, products, productPhases, dateStr);
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="${dateLocale().startsWith("en") ? "en" : "hr"}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${t("Radni nalog")} — ${esc(phaseName)} — ${fmtDate(dateStr)}</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
  ${pages}
  <script>
    window.addEventListener("load", function () {
      setTimeout(function () { window.print(); }, 250);
    });
  </script>
</body>
</html>`;
};
